<template>
    <div class="action-bar-light">
        <a aria-label="Flag Content"
           class="relative inline-flex items-center gap-2 px-3 py-2 mx-auto mb-8 text-xs bg-white rounded-sm focus:z-10"
           href="#"
           v-on:click.prevent="flag">
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4 shrink-0 opacity-80 text-inherit" viewBox="0 0 448 512">
                <path class="fa-secondary" opacity=".4" d="M64 352L433 213.6c9-3.4 15-12 15-21.6s-6-18.2-15-21.6L64 32V352z"/><path class="fa-primary" d="M32 0C49.7 0 64 14.3 64 32V480c0 17.7-14.3 32-32 32s-32-14.3-32-32V32C0 14.3 14.3 0 32 0z"/></svg>
            Flag Content 
        </a>
    </div>
    
    <TransitionRoot :show="isOpen" as="template">
        <Dialog as="div" class="relative z-10" @close="closeConfirmation">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                             leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"/>
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex items-end justify-center min-h-full p-4 sm:items-center sm:p-0">
                    <DialogPanel
                            class="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                        <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                            <button class="text-gray-400 bg-white rounded-md hover:text-gray-600 focus:outline-hidden"
                                    type="button"
                                    @click="closeConfirmation">
                                <span class="sr-only">Close</span>
                                <svg class="w-6 h-6" fill="none" stroke="currentColor"
                                     stroke-width="1.5" viewBox="0 0 24 24"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>

                            </button>
                        </div>
                        <div v-if="flagSuccessful"
                             class="min-h-full px-4 py-16 bg-white sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">

                            <div class="min-w-full mx-auto text-center">
                                <div class="space-y-3 ">
                                    <h1 class="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">Thank
                                        you!</h1>
                                    <p class="my-4 text-xl leading-6 text-clay-800">We have received your report.</p>
                                </div>
                            </div>

                        </div>
                        <div v-else>
                            <div class="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                                <FlagIcon aria-hidden="true" class="w-6 h-6 text-red-600"/>
                            </div>
                            <div class="mt-3 sm:mt-5">
                                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-center text-gray-900">
                                    Flag this item
                                </DialogTitle>
                                <ErrorSummary :response-status="responseStatus" class="mt-2"/>

                                <div class="mt-2">
                                    <label class="block text-sm font-medium text-gray-700" for="location">Reason for
                                        reporting</label>
                                    <select id="reason" v-model="request.reason" class="block w-full py-2 pl-3 pr-10 mt-1 text-base border rounded-md border-clay-500 focus:border-slate-700 focus:outline-hidden focus:ring-slate-700 sm:text-sm"
                                            name="reason">
                                        <option v-for="r in reasons" :key="r">{{ r }}</option>
                                    </select>
                                </div>
                                <div class="mt-2">
                                    <label class="block text-sm font-medium text-gray-700" for="details">Please provide
                                        specific details below</label>
                                    <div class="mt-1">
                                        <textarea id="details" v-model="request.details" class="block w-full border rounded-md shadow-sm border-clay-500 focus:border-slate-700 focus:ring-slate-700 sm:text-sm" name="details"
                                                  rows="4"></textarea>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">

                                <button class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border rounded-md shadow-sm border-clay-500 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-slate-700 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm" type="button"
                                        @click.prevent="closeConfirmation">
                                    Cancel
                                </button>
                                <SaButton @click.prevent="report" :loading="isSubmitting" variant="primary">
                                    <template #loading>
                                        <span class="font-semibold">Reporting...</span>
                                    </template>
                                    <span class="font-semibold">Report</span>
                                </SaButton>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

</template>

<script lang="ts" setup>
import {defineProps, Ref, ref} from "vue";
import {FlagIcon} from '@heroicons/vue/20/solid';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {store} from "@/store";
import {client, trackEvent} from '@/shared';
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import {FlagContent} from "@/dtos";
import SaButton from "@/main/components/SaButton.vue";

const props = defineProps<{
    url: string,
    metadata: {}
}>();

let reasons = [
    'Spam',
    'Incorrect information',
    'Contains nudity, explicit content or violent imagery',
    'Violates privacy standards',
    'I posted this content and want to delete it'
];
let responseStatus = ref({});
let flagSuccessful: Ref<Boolean> = ref(false);

let request = ref(new FlagContent({
    url: props.url,
    reason: '',
    details: '',
    metadata: props.metadata
}));

let isOpen = ref(false);
const closeConfirmation = async () => {
    isOpen.value = false;
}
const flag = async () => {
    if (!store.saUserSession.isAuthenticated) {
        store.showLoginDialog = true;
        return;
    }
    isOpen.value = true;
};

const isSubmitting = ref(false);
const report = async () => {
    if (!store.saUserSession.isAuthenticated) {
        store.showLoginDialog = true;
        return;
    }
    isSubmitting.value = true;
    await client.post<ResponseStatus>(new FlagContent(request.value))
        .then(_ => {
            trackEvent( 'content_flagged', request.value);
            flagSuccessful.value = true;
            responseStatus.value = {};
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
        });

    isSubmitting.value = false;
}
</script>